import { AlertCircle, CheckCircle2, RefreshCw, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ShoppingList } from '@/types/shopping-list'
import { ingredientParser } from '@/services/ingredients'

type SavedShoppingItem = {
  name: string
  quantity?: number
  unit?: string
  specification?: string
  recipe_titles: string[]
  is_purchased: boolean
  checked: boolean
  notes?: string
}

interface SavedShoppingListProps {
  loadingShoppingList: boolean
  currentMealPlanId: string
  currentShoppingList: ShoppingList | null
  savedItems: SavedShoppingItem[]
  onRefresh: (mealPlanId: string) => Promise<void>
  onToggleItemChecked: (itemId: string) => Promise<boolean>
  onSwitchToGenerate: () => void
  formatDate: (value: string) => string
}

export function SavedShoppingList({
  loadingShoppingList,
  currentMealPlanId,
  currentShoppingList,
  savedItems,
  onRefresh,
  onToggleItemChecked,
  onSwitchToGenerate,
  formatDate,
}: SavedShoppingListProps) {
  const purchasedCount = savedItems.filter((item) => item.is_purchased || item.checked).length

  if (loadingShoppingList) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
        <RefreshCw className="mx-auto mb-3 h-5 w-5 animate-spin text-gray-500" />
        <p className="text-sm text-gray-600">Boodschappenlijst laden...</p>
      </div>
    )
  }

  if (!currentShoppingList || savedItems.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-gray-400" />
        <h3 className="mb-2 font-semibold text-gray-900">Nog geen boodschappenlijst</h3>
        <p className="mb-4 text-sm text-gray-600">Genereer eerst een nieuwe lijst vanuit je huidige weekplanning.</p>
        <Button onClick={onSwitchToGenerate}>
          <Wand2 className="mr-2 h-4 w-4" />
          Nieuwe lijst maken
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-medium text-gray-900">{currentShoppingList.name}</div>
          <div className="mt-1 text-sm text-gray-600">
            {savedItems.length} items
            {currentShoppingList.start_date && currentShoppingList.end_date && (
              <span>
                {' '}voor {formatDate(currentShoppingList.start_date)} t/m {formatDate(currentShoppingList.end_date)}
              </span>
            )}
            <span>{` • ${purchasedCount}/${savedItems.length} gekocht`}</span>
          </div>
        </div>

        <Button onClick={() => void onRefresh(currentMealPlanId)} variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          Vernieuwen
        </Button>
      </div>

      <div className="space-y-2">
        {savedItems.map((item, index) => {
          const completed = item.is_purchased || item.checked

          return (
            <div
              key={`saved-item-${index}`}
              className={`flex items-start gap-3 rounded-lg border p-3 ${
                completed ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
              }`}
            >
              <button
                onClick={() => void onToggleItemChecked(`item-${index}`)}
                className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${
                  completed ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                }`}
                aria-label={completed ? 'Markeer als niet gekocht' : 'Markeer als gekocht'}
              >
                {completed && <CheckCircle2 className="h-3.5 w-3.5" />}
              </button>

              <div className="min-w-0 flex-1">
                <div className={`font-medium ${completed ? 'text-green-800 line-through' : 'text-gray-900'}`}>
                  {ingredientParser.formatShoppingListItem(item)}
                </div>
                {item.recipe_titles.length > 0 && <div className="mt-1 text-sm text-gray-600">{item.recipe_titles.join(', ')}</div>}
                {item.notes && <div className="mt-1 text-xs text-gray-500">{item.notes}</div>}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
